import React, { useState, useEffect } from 'react';
import { useAllTasks } from '../hooks/useAllTasks';
import { MagnifyingGlassIcon as SearchIcon, XCircleIcon } from '@heroicons/react/24/solid';
import './TaskSelector.css';

/**
 * Task selector component for timer panel
 * Allows selecting tasks from all projects
 */
const TaskSelector = ({ onTaskSelect, selectedTaskId = null }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  
  // Fetch all tasks with the ability to refetch
  const { tasks, isLoading, error, projects, refetch } = useAllTasks();
  
  // Refetch tasks when dropdown is opened
  const handleOpenDropdown = () => {
    // Refresh task data when opening dropdown
    refetch();
    setIsOpen(true);
  };
  
  // Filter tasks based on search term
  const filteredTasks = searchTerm 
    ? tasks.filter(task => 
        task.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.description?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : tasks;
  
  // Find selected task data
  const selectedTask = selectedTaskId 
    ? tasks.find(task => task.id === selectedTaskId) 
    : null;
  
  // Refresh tasks when dropdown is opened
  useEffect(() => {
    if (isOpen) {
      refetch();
    }
  }, [isOpen, refetch]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest('.task-selector')) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);
  
  // Group tasks by project for the dropdown
  const tasksByProject = filteredTasks.reduce((acc, task) => {
    // Skip tasks without project ID
    if (!task.project_id) return acc;
    
    const projectId = task.project_id;
    if (!acc[projectId]) {
      acc[projectId] = {
        projectName: task.project_name || projects.find(p => p.id === projectId)?.name || 'Unknown Project',
        tasks: []
      };
    }
    acc[projectId].tasks.push(task);
    return acc;
  }, {});
  
  return (
    <div className="task-selector relative">
      {/* Selected task display or selector button */}
      {selectedTask ? (
        <div className="flex items-center justify-between bg-white dark:bg-darkbg-700 p-3 rounded-md border border-grayteck-300 dark:border-darkbg-600">
          <div>
            <p className="font-medium text-grayteck-800 dark:text-grayteck-200">
              {selectedTask.name || 'Selected Task'}
            </p>
            {selectedTask.project_id && (
              <p className="text-sm text-grayteck-500 dark:text-grayteck-400">
                {selectedTask.project_name || projects.find(p => p.id === selectedTask.project_id)?.name || 'Unknown Project'}
              </p>
            )}
          </div>
          <button 
            onClick={() => {
              onTaskSelect(null);
              setIsOpen(true);
            }}
            className="text-grayteck-500 hover:text-grayteck-700 dark:text-grayteck-400 dark:hover:text-grayteck-200"
          >
            <XCircleIcon className="h-5 w-5" />
          </button>
        </div>
      ) : (
        <button
          onClick={handleOpenDropdown}
          className="w-full text-left px-3 py-2 border border-grayteck-300 dark:border-darkbg-600 
                   rounded-md bg-white dark:bg-darkbg-700 text-grayteck-700 dark:text-grayteck-200
                   hover:bg-grayteck-50 dark:hover:bg-darkbg-600 focus:outline-none focus:ring-2 focus:ring-grayteck-500"
        >
          Select a task...
        </button>
      )}
      
      {/* Task dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-darkbg-800 border border-grayteck-300 dark:border-darkbg-600 rounded-md shadow-lg task-dropdown">
          {/* Search input - positioned sticky at top */}
          <div className="p-2 border-b border-grayteck-200 dark:border-darkbg-700 sticky top-0 left-0 right-0 bg-white dark:bg-darkbg-800 z-10">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SearchIcon className="h-4 w-4 text-grayteck-500 dark:text-grayteck-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-grayteck-300 dark:border-darkbg-600 
                        rounded-md bg-white dark:bg-darkbg-700 text-grayteck-800 dark:text-grayteck-200
                        focus:outline-none focus:ring-2 focus:ring-grayteck-500"
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setSearchTerm('')}
                >
                  <XCircleIcon className="h-5 w-5 text-grayteck-400 hover:text-grayteck-500 dark:text-grayteck-500 dark:hover:text-grayteck-400" />
                </button>
              )}
            </div>
          </div>
          
          {/* Loading state */}
          {isLoading && (
            <div className="p-4 text-center text-grayteck-500 dark:text-grayteck-400">
              Loading tasks...
            </div>
          )}
          
          {/* Error state */}
          {error && (
            <div className="p-4 text-center text-red-500">
              Error loading tasks: {error.message}
            </div>
          )}
          
          {/* Empty state */}
          {!isLoading && !error && filteredTasks.length === 0 && (
            <div className="p-4 text-center">
              {searchTerm ? (
                <p className="text-grayteck-500 dark:text-grayteck-400">No tasks match your search</p>
              ) : (
                <div>
                  <p className="text-grayteck-600 dark:text-grayteck-300 mb-1">No tasks available</p>
                  <p className="text-sm text-grayteck-500 dark:text-grayteck-400">
                    Use the "Create New Task" button below to add a task
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Task list grouped by project - more compact */}
          <div className="task-list-container">
            {!isLoading && !error && Object.entries(tasksByProject).map(([projectId, { projectName, tasks }]) => (
              <div key={projectId} className="task-project-group">
                <div className="px-3 py-1 bg-grayteck-100 dark:bg-darkbg-700 text-grayteck-700 dark:text-grayteck-300 font-medium text-xs sticky left-0">
                  {projectName}
                </div>
                {tasks.map(task => (
                  <button
                    key={task.id}
                    className="w-full text-left px-3 py-1.5 hover:bg-grayteck-100 dark:hover:bg-darkbg-700 
                            text-grayteck-800 dark:text-grayteck-200 focus:outline-none"
                    onClick={() => {
                      onTaskSelect(task);
                      setIsOpen(false);
                    }}
                  >
                    <div className="flex items-center text-sm">
                      <div className="flex-1 truncate pr-2">
                        <p className="font-medium truncate">{task.name}</p>
                      </div>
                      <div className="flex-shrink-0">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-sm text-xs font-medium
                                      ${task.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100' :
                                        task.status === 'in_progress' ? 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100' :
                                        'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'}`}>
                          {task.status === 'to_do' ? 'To Do' :
                           task.status === 'in_progress' ? 'In Progress' :
                           task.status === 'completed' ? 'Completed' :
                           task.status === 'blocked' ? 'Blocked' :
                           task.status === 'cancelled' ? 'Cancelled' :
                           task.status}
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskSelector;